/**
 * The slider every panel is built from.
 *
 * Three things it does that a plain range input does not, and that matter for
 * this audience:
 *  - the fill runs from the control's *neutral* value, not from the left edge,
 *    so a glance tells you both how far a control has moved and which way;
 *  - drag is pointer-locked and pixel-rate-scaled, with a fine mode on shift,
 *    because a 0.05 EV exposure move is a real edit;
 *  - double-click returns to neutral, and the number is directly typeable.
 */

import { useCallback, useRef, useState } from 'react';
import { useStore } from '../../state/store.ts';
import { Tip } from './Tooltip.tsx';

export interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  /** Value that counts as "no change"; the fill starts here. */
  neutral?: number;
  unit?: string;
  /** Decimal places for the readout. Derived from `step` when omitted. */
  decimals?: number;
  /** CSS gradient painted behind the track, for temperature / hue controls. */
  gradient?: string;
  /**
   * What the control does, in a sentence. Shown on hover or focus, and inline
   * only when the user has turned explanations on.
   */
  help?: string;
  disabled?: boolean;
  onChange: (value: number) => void;
  /** Fired on pointer release / blur — the point at which history is written. */
  onCommit?: () => void;
}

export function Slider({
  label,
  value,
  min,
  max,
  step,
  neutral = 0,
  unit = '',
  decimals,
  gradient,
  help,
  disabled = false,
  onChange,
  onCommit,
}: SliderProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ startX: number; startValue: number } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  const showHelp = useStore((state) => state.showHelp);
  const places = decimals ?? (step < 0.05 ? 2 : step < 1 ? 1 : 0);
  const range = max - min;
  const toFraction = (v: number) => (v - min) / range;
  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  const quantise = (v: number) => Math.round(v / step) * step;

  const changed = Math.abs(value - neutral) > step / 2;

  const applyFromPointer = useCallback(
    (clientX: number, fine: boolean) => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const state = dragState.current;

      if (fine && state) {
        // Fine mode: a quarter of a pixel-rate move, relative to where the
        // drag started, so precision does not depend on where you grabbed.
        const delta = ((clientX - state.startX) / rect.width) * range * 0.25;
        onChange(clamp(quantise(state.startValue + delta)));
        return;
      }
      const fraction = (clientX - rect.left) / rect.width;
      onChange(clamp(quantise(min + fraction * range)));
    },
    [min, range, step, onChange],
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    event.preventDefault();
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
    dragState.current = { startX: event.clientX, startValue: value };
    applyFromPointer(event.clientX, event.shiftKey);
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled || !dragState.current) return;
    applyFromPointer(event.clientX, event.shiftKey);
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragState.current) return;
    dragState.current = null;
    (event.target as HTMLElement).releasePointerCapture?.(event.pointerId);
    onCommit?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled) return;
    const large = event.shiftKey ? 10 : 1;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      onChange(clamp(quantise(value - step * large)));
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      onChange(clamp(quantise(value + step * large)));
    } else if (event.key === 'Home') {
      event.preventDefault();
      onChange(neutral);
      onCommit?.();
    } else if (event.key === 'Enter') {
      onCommit?.();
    }
  };

  const commitTyped = () => {
    if (editing === null) return;
    const parsed = Number.parseFloat(editing.replace(/[^0-9.+-]/g, ''));
    setEditing(null);
    if (Number.isFinite(parsed)) {
      onChange(clamp(quantise(parsed)));
      onCommit?.();
    }
  };

  const fillLeft = Math.min(toFraction(neutral), toFraction(value)) * 100;
  const fillWidth = Math.abs(toFraction(value) - toFraction(neutral)) * 100;

  return (
    <div className={`slider${changed ? ' changed' : ''}`}>
      <div className="slider-top">
        {help ? (
          <Tip title={label} body={help} className="slider-name">
            {label}
          </Tip>
        ) : (
          <span className="slider-name">{label}</span>
        )}
        <input
          className="slider-value"
          value={editing ?? `${value > neutral && neutral === 0 ? '+' : ''}${value.toFixed(places)}${unit}`}
          onChange={(event) => setEditing(event.target.value)}
          onFocus={(event) => {
            setEditing(value.toFixed(places));
            event.target.select();
          }}
          onBlur={commitTyped}
          onKeyDown={(event) => {
            if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
            if (event.key === 'Escape') {
              setEditing(null);
              (event.target as HTMLInputElement).blur();
            }
          }}
          disabled={disabled}
          aria-label={`${label} value`}
        />
      </div>
      <div
        ref={trackRef}
        className="slider-track"
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={label}
        aria-valuenow={Number(value.toFixed(places))}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuetext={`${value.toFixed(places)}${unit}`}
        aria-disabled={disabled}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={handleKeyDown}
        onDoubleClick={() => {
          onChange(neutral);
          onCommit?.();
        }}
      >
        {gradient ? <div className="slider-gradient" style={{ background: gradient }} /> : <div className="slider-rail" />}
        <div className="slider-fill" style={{ left: `${fillLeft}%`, width: `${fillWidth}%` }} />
        <div className="slider-neutral" style={{ left: `${toFraction(neutral) * 100}%` }} />
        <div className="slider-thumb" style={{ left: `${toFraction(value) * 100}%` }} />
      </div>
      {help && showHelp ? <div className="slider-help">{help}</div> : null}
    </div>
  );
}

